import { useT } from '@/i18n/runtime';
import type { TenantConfig } from '@/types';
import type { GeoGateState } from '@/media/useGeoEvidence';
import { AppBar } from './AppBar';
import { Icon } from './Icon';
import { Button } from './Button';

/**
 * GEO GATE — the permission-primed live-location step shown on the call screen
 * BEFORE the media join (S5-9; RBI-04). Explains WHY before the browser prompt
 * ever fires (permission priming: an unexplained prompt reads as spyware and
 * gets denied), and renders honest degrade states:
 *
 *   • denied      → "the call can still go ahead; your officer will see that
 *                    location evidence is missing" + retry/continue — never a
 *                    silent block, never a dead end (enforcement is the
 *                    officer's, not the browser's).
 *   • degraded    → unsupported/timeout/error with the same honest continue.
 *   • post-failed → captured but not recorded; retry or continue.
 *
 * Tokens/classes only (Dataroom mode) — no hardcoded colours.
 */
export function GeoGate({ tenant, gate }: { tenant: TenantConfig; gate: GeoGateState }): JSX.Element {
  const { t } = useT();
  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.live.subtitle')} />

      <div className="app-view center" style={{ paddingTop: 24 }}>
        <div className="center-col" style={{ maxWidth: '34ch' }}>
          <span
            style={{
              display: 'inline-flex',
              padding: 14,
              borderRadius: '50%',
              background: 'var(--surface-elevated)',
              marginBottom: 14,
            }}
          >
            <Icon name="mappin" size={30} />
          </span>

          {gate.phase === 'explain' ? (
            <>
              <div className="h-title sm">{t('droom.geo.explain.title')}</div>
              <p className="sub" style={{ marginTop: 8 }}>
                {t(
                  tenant.regime === 'IFSCA'
                    ? 'droom.geo.explain.body.ifsca'
                    : tenant.regime === 'RBI'
                      ? 'droom.geo.explain.body'
                      : 'droom.geo.explain.body.tenant',
                )}
              </p>
              <Button style={{ marginTop: 18 }} onClick={gate.share}>
                {t('droom.geo.explain.cta')}
              </Button>
            </>
          ) : null}

          {gate.phase === 'requesting' || gate.phase === 'posting' || gate.phase === 'captured' ? (
            <>
              <div className="h-title sm">
                {gate.phase === 'captured' ? t('droom.geo.captured.title') : t('droom.geo.getting.title')}
              </div>
              <p className="sub" style={{ marginTop: 8 }} role="status">
                {gate.phase === 'captured'
                  ? t('droom.geo.captured.body')
                  : t('droom.geo.getting.body')}
              </p>
            </>
          ) : null}

          {gate.phase === 'denied' ? (
            <>
              <div className="h-title sm">{t('droom.geo.denied.title')}</div>
              <p className="sub" style={{ marginTop: 8 }}>
                {t('droom.geo.denied.body')}
              </p>
              {/* DEC-060(A): the concrete steps — the officer reads the same guidance on their console,
                  so the two sides can walk through an inadvertent "Block" together. */}
              <p className="sub" style={{ marginTop: 8 }}>
                {t('droom.geo.denied.howto')}
              </p>
              <Button style={{ marginTop: 18 }} onClick={gate.retry}>
                {t('droom.action.tryAgain')}
              </Button>
              <Button variant="ghost" style={{ marginTop: 8 }} onClick={gate.continueWithout}>
                {t('droom.geo.without')}
              </Button>
            </>
          ) : null}

          {gate.phase === 'degraded' || gate.phase === 'post-failed' ? (
            <>
              <div className="h-title sm">
                {gate.phase === 'post-failed'
                  ? t('droom.geo.postfailed.title')
                  : t('droom.geo.degraded.title')}
              </div>
              <p className="sub" style={{ marginTop: 8 }}>
                {gate.detail ?? t('droom.geo.degraded.fallback')}{' '}
                {t('droom.geo.degraded.body')}
              </p>
              <Button style={{ marginTop: 18 }} onClick={gate.retry}>
                {t('droom.action.tryAgain')}
              </Button>
              <Button variant="ghost" style={{ marginTop: 8 }} onClick={gate.continueWithout}>
                {t('droom.geo.without')}
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
